import {
  VIEW_MODE_CHANNEL_BANNER,
  VIEW_MODE_CHANNEL_GUIDE,
  VIEW_MODE_CHANNEL_VIEW,
  TOGGLE_MUTED,
  FULLSCREEN_MODE,
} from 'constants/channels'
import api from 'libs/api'
import auth from 'libs/auth'
import {
  createDummyProgram,
  getClosedCaptionsForChannelId,
  getRememberedChannelId,
  getRequestedViewMode,
  hideClosedCaptionsForChannelId,
  rememberChannelId,
  showClosedCaptionsForChannelId,
} from 'libs/channels'
import perf from 'libs/perf'
import { roundToNearestHalfHour } from 'libs/time'
import { getP } from 'redux-polyglot'
import { addTimeout, removeTimeout } from 'redux-timeout'
import { selectRadioModuleEnabled } from 'selectors/app'
import {
  getAllPrograms,
  getChannelPackageById,
  getFilteredChannelIds,
  getProgramsIsFetching,
  getProgramsTo,
  getSelectedId,
  getSocialBroadcasters,
  getViewMode,
} from 'selectors/channels'
import canAutoPlay from 'can-autoplay'

/**
 * Actions for loading the list of channel and the program guide data
 */
export const INIT_CHANNELS = 'CHANNELS/INIT_CHANNELS'

const initChannelsRequest = () => ({
  type: INIT_CHANNELS,
})

export const initChannels = () => (dispatch, getState) => {
  if (getSocialBroadcasters(getState())) {
    dispatch(resetSocialBroadcasters())
  }
  dispatch(initChannelsRequest())
  return dispatch(loadChannels())
    .then(() =>
      dispatch(selectChannel(getRememberedChannelId(), VIEW_MODE_CHANNEL_GUIDE))
    )
    .then(() => dispatch(loadPrograms()))
}

/**
 * Actions for dismissing an error message
 */
export const DISMISS_ERROR = 'CHANNELS/DISMISS_ERROR'
export const dismissError = () => ({
  type: DISMISS_ERROR,
})

/**
 * Actions for fetching channel list from API
 */
export const FETCH_CHANNELS_REQUEST = 'CHANNELS/FETCH_CHANNELS_REQUEST'
export const FETCH_CHANNELS_SUCCESS = 'CHANNELS/FETCH_CHANNELS_SUCCESS'
export const FETCH_CHANNELS_FAILURE = 'CHANNELS/FETCH_CHANNELS_FAILURE'

const fetchChannelsRequest = () => ({
  type: FETCH_CHANNELS_REQUEST,
})

const fetchChannelsSuccess = payload => ({
  type: FETCH_CHANNELS_SUCCESS,
  payload,
})

const fetchChannelsFailure = error => ({
  type: FETCH_CHANNELS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
})

const loadLiveChannels = () => (dispatch, getState) => {
  perf.start('channels:list:fetch')
  dispatch(fetchChannelsRequest())
  return api
    .getChannels(getSocialBroadcasters(getState()))
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          let channels = {
            byId: {},
            byNo: {},
          }
          response.channels.forEach(channel => {
            const processed = {
              id: channel.id,
              type: 'live',
              isLive: !!channel.live,
              no: channel.stb_number,
              name: channel.name,
              img: channel.img.large,
              dvr: !!channel.dvr,
              category: channel.category ? channel.category.name : '',
              isPremium: !!channel.is_premium,
              hasAccess: !!channel.has_access,
            }
            channels.byId[processed.id] = processed
            channels.byNo[processed.no] = processed.id
          })
          perf.stop('channels:list:fetch', 'retrieved list of channels from server')
          return dispatch(fetchChannelsSuccess(channels))
        } else {
          return dispatch(fetchChannelsFailure(response.reason))
        }
      },
      () =>
        dispatch(
          fetchChannelsFailure(
            getP(getState()).t('tv_channels.error_retrieving_channel_list', {
              _: 'An error occurred while retrieving channel list',
            })
          )
        )
    )
}

/**
 * Actions for fetching radio list from API
 */
export const FETCH_RADIOS_REQUEST = 'RADIOS/FETCH_RADIOS_REQUEST'
export const FETCH_RADIOS_SUCCESS = 'RADIOS/FETCH_RADIOS_SUCCESS'

const fetchRadiosRequest = () => ({
  type: FETCH_RADIOS_REQUEST,
})

const fetchRadiosSuccess = payload => ({
  type: FETCH_RADIOS_SUCCESS,
  payload,
})

const getInitialRadiosPayload = _ => ({
  byId: {},
  byNo: {},
})

const loadRadioChannels = () => (dispatch, getState) => {
  perf.start('radios:list:fetch')
  dispatch(fetchRadiosRequest())
  return api
    .getRadios(getSocialBroadcasters(getState()))
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          let radios = getInitialRadiosPayload()
          response.radios.forEach(radio => {
            const processed = {
              id: radio.id,
              type: 'radio',
              genre: radio.genre.name,
              desc: radio.description,
              isLive: !!radio.live,
              no: radio.stb_number,
              name: radio.name,
              img: radio.img.large,
              dvr: false,
            }
            radios.byId[processed.id] = processed
            radios.byNo[processed.no] = processed.id
          })
          perf.stop('radios:list:fetch', 'retrieved list of radios from server')
          return dispatch(fetchRadiosSuccess(radios))
        } else {
          return dispatch(fetchRadiosSuccess(getInitialRadiosPayload()))
        }
      },
      () => dispatch(fetchRadiosSuccess(getInitialRadiosPayload()))
    )
}

/**
 * Actions for fetching all channels
 */
export const loadChannels = () => (dispatch, getState) => {
  const radioModuleEnabled = selectRadioModuleEnabled(getState())
  if (radioModuleEnabled) {
    return dispatch(loadLiveChannels()).then(() => dispatch(loadRadioChannels()))
  } else {
    return dispatch(loadLiveChannels())
  }
}

/**
 * Actions for fetching channel package details from API
 */
export const FETCH_CHANNEL_PACKAGES_REQUEST = 'CHANNELS/FETCH/CHANNEL_PACKAGES/REQUEST'
export const FETCH_CHANNEL_PACKAGES_SUCCESS = 'CHANNELS/FETCH/CHANNEL_PACKAGES/SUCCESS'
export const FETCH_CHANNEL_PACKAGES_FAILURE = 'CHANNELS/FETCH/CHANNEL_PACKAGES/FAILURE'

const fetchChannelPackagesRequest = id => ({
  type: FETCH_CHANNEL_PACKAGES_REQUEST,
  payload: {
    id: id,
  },
})

const fetchChannelPackagesSuccess = (channelId, packageIds) => ({
  type: FETCH_CHANNEL_PACKAGES_SUCCESS,
  payload: {
    channelId,
    packages: packageIds,
  },
})

const fetchChannelPackagesFailure = (id, error) => ({
  type: FETCH_CHANNEL_PACKAGES_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  meta: {
    id: id,
  },
  error: true,
})

const loadChannelPackages = id => (dispatch, getState) => {
  dispatch(fetchChannelPackagesRequest(id))
  return api
    .getChannelPackages(id)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          if (response.options.length > 0) {
            const packages = []
            const requests = []
            response.options.forEach(pkg => {
              packages.push(pkg.id)
              if (!getChannelPackageById(getState(), pkg.id)) {
                requests.push(dispatch(loadChannelPackage(id, pkg.id)))
              }
            })
            return Promise.all(requests).then(() =>
              dispatch(fetchChannelPackagesSuccess(id, packages))
            )
          } else {
            return dispatch(
              fetchChannelPackagesFailure(
                id,
                getP(getState()).t('tv_channels.error_retrieving_channel_packages', {
                  _: 'An error occurred while retrieving channel packages',
                })
              )
            )
          }
        } else {
          return dispatch(fetchChannelPackagesFailure(id, response.reason))
        }
      },
      () =>
        dispatch(
          fetchChannelPackagesFailure(
            id,
            getP(getState()).t('tv_channels.error_retrieving_channel_packages', {
              _: 'An error occurred while retrieving channel packages',
            })
          )
        )
    )
}

export const FETCH_PACKAGE_DETAILS_REQUEST = 'CHANNELS/FETCH/PACKAGE_DETAILS/REQUEST'
export const FETCH_PACKAGE_DETAILS_SUCCESS = 'CHANNELS/FETCH/PACKAGE_DETAILS/SUCCESS'
export const FETCH_PACKAGE_DETAILS_FAILURE = 'CHANNELS/FETCH/PACKAGE_DETAILS/FAILURE'

const fetchChannelPackageRequest = (channelId, packageId) => ({
  type: FETCH_PACKAGE_DETAILS_REQUEST,
  payload: {
    channelId,
    packageId,
  },
})

const fetchChannelPackageSuccess = (channelId, packageId, pkg) => ({
  type: FETCH_PACKAGE_DETAILS_SUCCESS,
  payload: {
    channelId,
    packageId,
    pkg,
  },
})

const fetchChannelPackageFailure = (channelId, packageId, error) => ({
  type: FETCH_PACKAGE_DETAILS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  meta: {
    channelId,
    packageId,
  },
  error: true,
})

const processPackageDetails = pkg => {
  const channels = []
  let count = 0
  if (pkg.live_channels && pkg.live_channels.length > 0) {
    count += pkg.live_channels.length
    pkg.live_channels.forEach(channel => {
      channels.length < 24 &&
        channels.push({
          id: channel.id,
          name: channel.name,
          img: channel.img.small,
        })
    })
  }
  if (pkg.catchup_channels && pkg.catchup_channels.length > 0) {
    count += pkg.catchup_channels.length
    if (channels.length < 24) {
      pkg.catchup_channels.forEach(channel => {
        channels.length < 24 &&
          channels.push({
            id: channel.id,
            name: channel.name,
            img: channel.img.small,
          })
      })
    }
  }
  if (pkg.audio_channels && pkg.audio_channels.length > 0) {
    count += pkg.audio_channels.length
    if (channels.length < 24) {
      pkg.audio_channels.forEach(channel => {
        channels.length < 24 &&
          channels.push({
            id: channel.id,
            name: channel.name,
            img: channel.img.small,
          })
      })
    }
  }
  return {
    id: parseInt(pkg.id, 10),
    name: pkg.name,
    img: pkg.img.medium,
    channels: channels,
    count: count,
    price: pkg.price,
    recurring: pkg.recurring,
    recurDetails: {
      length: pkg.recurring_length,
      type: pkg.recurring_type,
    },
  }
}

const loadChannelPackage = (channelId, packageId) => (dispatch, getState) => {
  dispatch(fetchChannelPackageRequest(channelId, packageId))
  return api
    .getPackage(packageId)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200 && response.details.available_on_html5 === 'yes') {
          return dispatch(
            fetchChannelPackageSuccess(
              channelId,
              packageId,
              processPackageDetails(response.details)
            )
          )
        } else {
          return dispatch(
            fetchChannelPackageFailure(channelId, packageId, response.reason)
          )
        }
      },
      () =>
        dispatch(
          fetchChannelPackageFailure(
            channelId,
            packageId,
            getP(getState()).t('tv_channels.error_retrieving_channel_package_details', {
              _: 'An error occurred while retrieving channel package details',
            })
          )
        )
    )
}

export const FETCH_PREMIUM_DETAILS_REQUEST = 'CHANNELS/FETCH/PREMIUM_DETAILS/REQUEST'
export const FETCH_PREMIUM_DETAILS_SUCCESS = 'CHANNELS/FETCH/PREMIUM_DETAILS/SUCCESS'
export const FETCH_PREMIUM_DETAILS_FAILURE = 'CHANNELS/FETCH/PREMIUM_DETAILS/FAILURE'

const fetchPremiumDetailsRequest = channelId => ({
  type: FETCH_PREMIUM_DETAILS_REQUEST,
  payload: {
    channelId,
  },
})

const fetchPremiumDetailsSuccess = (channelId, details, videos, videoCount) => ({
  type: FETCH_PREMIUM_DETAILS_SUCCESS,
  payload: {
    channelId,
    details: {
      desc: details.description,
      price: details.price,
      recurring: details.recurring,
      recurDetails: {
        length: details.recurring_length,
        type: details.recurring_type,
      },
      videoCount,
      videos: videos.map(video => ({
        id: video.id,
        name: video.name,
        img: video.img.small,
      })),
      offersPage: !!details.offers_page,
    },
  },
})

const fetchPremiumDetailsFailure = (channelId, error) => ({
  type: FETCH_PREMIUM_DETAILS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  meta: {
    channelId,
  },
  error: true,
})

const loadPremiumDetails = channelId => (dispatch, getState) => {
  dispatch(fetchPremiumDetailsRequest(channelId))
  return api
    .getPremiumDetails(channelId)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return api
            .getAddonVideos(channelId, 8)
            .then(res => res.json())
            .then(
              vodResponse => {
                if (vodResponse.status === 200) {
                  return dispatch(
                    fetchPremiumDetailsSuccess(
                      channelId,
                      response['premium-channel'],
                      vodResponse['videos'],
                      vodResponse['pager'].total
                    )
                  )
                } else {
                  return dispatch(
                    fetchPremiumDetailsSuccess(channelId, response['premium-channel'])
                  )
                }
              },
              () =>
                dispatch(
                  fetchPremiumDetailsSuccess(channelId, response['premium-channel'])
                )
            )
        } else {
          return dispatch(fetchPremiumDetailsFailure(channelId, response.reason))
        }
      },
      () =>
        dispatch(
          fetchPremiumDetailsFailure(
            channelId,
            getP(getState()).t('tv_channels.error_retrieving_premium_channel_details', {
              _: 'An error occurred while retrieving premium channel details',
            })
          )
        )
    )
}

/**
 * Actions for fetching a specific channel details from API
 */
export const FETCH_CHANNEL_REQUEST = 'CHANNELS/FETCH_CHANNEL_REQUEST'
export const FETCH_CHANNEL_SUCCESS = 'CHANNELS/FETCH_CHANNEL_SUCCESS'
export const FETCH_CHANNEL_FAILURE = 'CHANNELS/FETCH_CHANNEL_FAILURE'
export const FETCH_CHANNEL_WARNING = 'CHANNELS/FETCH_CHANNEL_WARNING'

const fetchChannelRequest = id => ({
  type: FETCH_CHANNEL_REQUEST,
  payload: {
    id: id,
  },
})

const fetchChannelSuccess = payload => ({
  type: FETCH_CHANNEL_SUCCESS,
  payload,
})

const fetchChannelFailure = (id, error) => ({
  type: FETCH_CHANNEL_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  meta: {
    id: id,
  },
  error: true,
})

const fetchChannelWarning = (id, error) => ({
  type: FETCH_CHANNEL_WARNING,
  payload: error instanceof Error ? error : new Error(error),
  meta: {
    id: id,
  },
  error: true,
})

const fetchChannelSubscriptionFailure = (id, subscriptionAvailablePackages, error) => ({
  type: FETCH_CHANNEL_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  meta: {
    id: id,
    needsSubscription: true,
    subscriptionAvailablePackages,
  },
  error: true,
})

export const loadChannel = (id, skipErrors = false) => (dispatch, getState) => {
  perf.start('channel:details:fetch')
  if (!skipErrors) {
    dispatch(fetchChannelRequest(id))
  }
  return api
    .getChannel(id)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          if (response.channel.premium && !response.channel.has_subscription) {
            dispatch(
              fetchChannelSuccess({
                id: response.channel.id,
                type: 'live',
                isLive: !!response.channel.live,
                no: response.channel.stb_number,
                name: response.channel.name,
                img: response.channel.img.large,
                dvr: !!response.channel.dvr,
                category: response.channel.category ? response.channel.category.name : '',
                isPremium: true,
                hasAccess: false,
              })
            )
            if (response.channel.subscription_available_packages > 0) {
              return dispatch(loadPremiumDetails(id))
                .then(() => dispatch(loadChannelPackages(id)))
                .then(() =>
                  dispatch(
                    fetchChannelSubscriptionFailure(
                      id,
                      response.channel.subscription_available_packages,
                      response.channel.no_subscription_message
                    )
                  )
                )
            } else {
              return dispatch(loadPremiumDetails(id)).then(() =>
                dispatch(
                  fetchChannelSubscriptionFailure(
                    id,
                    response.channel.subscription_available_packages,
                    response.channel.no_subscription_message
                  )
                )
              )
            }
          } else {
            perf.stop('channel:details:fetch', 'retrieved channel details from server')
            return dispatch(
              fetchChannelSuccess({
                id: response.channel.id,
                type: 'live',
                isLive: !!response.channel.live,
                no: response.channel.stb_number,
                name: response.channel.name,
                img: response.channel.img.large,
                dvr: !!response.channel.dvr,
                category: response.channel.category ? response.channel.category.name : '',
                isPremium: true,
                hasAccess: false,
                url: response.channel.sources.hls,
              })
            )
          }
        } else {
          return dispatch(fetchChannelFailure(id, response.reason))
        }
      },
      err => {
        if (skipErrors) {
          return dispatch(fetchChannelWarning(id, err))
        } else {
          return dispatch(
            fetchChannelFailure(
              id,
              getP(getState()).t('tv_channels.error_retrieving_channel_details', {
                _: 'An error occurred while retrieving channel details',
              })
            )
          )
        }
      }
    )
}

/**
 * Actions for fetching a specific radio details from API
 */
export const FETCH_RADIO_REQUEST = 'RADIOS/FETCH_RADIO_REQUEST'
export const FETCH_RADIO_SUCCESS = 'RADIOS/FETCH_RADIO_SUCCESS'
export const FETCH_RADIO_FAILURE = 'RADIOS/FETCH_RADIO_FAILURE'

const fetchRadioRequest = id => ({
  type: FETCH_RADIO_REQUEST,
  payload: {
    id: id,
  },
})

const fetchRadioSuccess = payload => ({
  type: FETCH_RADIO_SUCCESS,
  payload,
})

const fetchRadioFailure = (id, error) => ({
  type: FETCH_RADIO_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  meta: {
    id: id,
  },
  error: true,
})

export const loadRadio = id => (dispatch, getState) => {
  perf.start('radio:details:fetch')
  dispatch(fetchRadioRequest(id))
  return api
    .getRadio(id)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          perf.stop('radio:details:fetch', 'retrieved radio details from server')
          return dispatch(
            fetchRadioSuccess({
              id: response.radio.id,
              type: 'radio',
              genre: response.radio.genre.name,
              desc: response.radio.description,
              isLive: !!response.radio.live,
              no: response.radio.stb_number,
              name: response.radio.name,
              img: response.radio.img.large,
              dvr: false,
              url: response.radio.stream,
            })
          )
        } else {
          return dispatch(fetchRadioFailure(id, response.reason))
        }
      },
      _ =>
        dispatch(
          fetchRadioFailure(
            id,
            getP(getState()).t('tv_radios.error_retrieving_radio_details', {
              _: 'An error occurred while retrieving radio details',
            })
          )
        )
    )
}

/**
 * Zapping actions
 */
export const SELECT_CHANNEL = 'CHANNELS/SELECT_CHANNEL'
export const SOFT_SELECT_CHANNEL = 'CHANNELS/SOFT_SELECT_CHANNEL'
export const FOCUS_CHANNEL = 'CHANNELS/FOCUS_CHANNEL'

export const selectChannel = (id, viewMode = VIEW_MODE_CHANNEL_VIEW) => {
  rememberChannelId(id)

  const action = {
    type: SELECT_CHANNEL,
    payload: {
      id,
      viewMode,
      closedCaptions: getClosedCaptionsForChannelId(id),
    },
  }

  if (viewMode === VIEW_MODE_CHANNEL_GUIDE) {
    action.payload.from = roundToNearestHalfHour(Math.floor(Date.now() / 1000))
    action.payload.duration = 72
  }

  return action
}

const softSelectChannel = id => {
  return {
    type: SOFT_SELECT_CHANNEL,
    payload: {
      id: id,
    },
  }
}

export const focusChannel = id => ({
  type: FOCUS_CHANNEL,
  payload: {
    id: id,
  },
})

let lastChangeChannelUp = 0
let lastChangeChannelDown = 0
let changeChannelTimer = null

const cancelChangeChannelTimer = () => {
  if (changeChannelTimer !== null) {
    clearTimeout(changeChannelTimer)
    changeChannelTimer = null
  }
}

export const changeChannelUp = () => (dispatch, getState) => {
  const now = Date.now()
  if (now - lastChangeChannelUp < 275) {
    return false
  }
  lastChangeChannelUp = now

  const channelIds = getFilteredChannelIds(getState())
  if (channelIds.length === 0) {
    return false
  }

  const state = getState().channels

  let nextPos
  if (state.selectedChannelBanner.channelId === -1) {
    if (state.selectedId === -1) {
      nextPos = 0
    } else {
      nextPos = channelIds.indexOf(state.selectedId) + 1
    }
  } else {
    nextPos = channelIds.indexOf(state.selectedChannelBanner.channelId) + 1
  }

  if (nextPos >= channelIds.length) {
    nextPos = 0
  }

  dispatch(softSelectChannel(channelIds[nextPos]))
  cancelChangeChannelTimer()
  changeChannelTimer = setTimeout(() => {
    dispatch(selectChannel(channelIds[nextPos]))
  }, 1000)
}

export const changeChannelDown = () => (dispatch, getState) => {
  const now = Date.now()
  if (now - lastChangeChannelDown < 275) {
    return false
  }
  lastChangeChannelDown = now

  const channelIds = getFilteredChannelIds(getState())
  if (channelIds.length === 0) {
    return false
  }

  const state = getState().channels

  let prevPos
  if (state.selectedChannelBanner.channelId === -1) {
    if (state.selectedId === -1) {
      prevPos = 0
    } else {
      prevPos = channelIds.indexOf(state.selectedId) - 1
    }
  } else {
    prevPos = channelIds.indexOf(state.selectedChannelBanner.channelId) - 1
  }

  if (prevPos < 0) {
    prevPos = channelIds.length - 1
  }

  dispatch(softSelectChannel(channelIds[prevPos]))
  cancelChangeChannelTimer()
  changeChannelTimer = setTimeout(() => {
    dispatch(selectChannel(channelIds[prevPos]))
  }, 1000)
}

/**
 * Sidebar related actions
 */
export const OPEN_CHANNEL_LIST = 'CHANNELS/OPEN_CHANNEL_LIST'
export const OPEN_FAVORITE_LIST = 'CHANNELS/OPEN_FAVORITE_LIST'
export const CLOSE_SIDEBAR = 'CHANNELS/CLOSE_SIDEBAR'

export const openChannelList = () => ({
  type: OPEN_CHANNEL_LIST,
})
export const openFavoriteList = () => ({
  type: OPEN_FAVORITE_LIST,
})
export const closeSidebar = () => ({
  type: CLOSE_SIDEBAR,
})

/**
 * Actions for retrieving the programs from the API
 */
export const FETCH_PROGRAMS_REQUEST = 'CHANNELS/FETCH_PROGRAMS_REQUEST'
export const FETCH_PROGRAMS_SUCCESS = 'CHANNELS/FETCH_PROGRAMS_SUCCESS'
export const FETCH_PROGRAMS_FAILURE = 'CHANNELS/FETCH_PROGRAMS_FAILURE'

const fetchProgramsRequest = () => ({
  type: FETCH_PROGRAMS_REQUEST,
})

const fetchProgramsSuccess = (programs, from, to) => ({
  type: FETCH_PROGRAMS_SUCCESS,
  payload: {
    programs,
    from,
    to,
  },
})

const fetchProgramsFailure = error => ({
  type: FETCH_PROGRAMS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
})

export const loadPrograms = (requestedFrom = null) => (dispatch, getState) => {
  perf.start('channels:guide:fetch')
  dispatch(fetchProgramsRequest())

  const now = new Date()
  now.setMinutes(0, 0, 0)
  const from = requestedFrom !== null ? requestedFrom : Math.floor(now.getTime() / 1000)
  const to = from + 24 * 3600
  const isLazyLoad = requestedFrom !== null

  return api
    .getPrograms(from, to, getSocialBroadcasters(getState()))
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          const allPrograms = getAllPrograms(getState())
          const programs = isLazyLoad ? { ...allPrograms } : {}
          response.channels.forEach(channel => {
            let previousProgram = null
            programs[channel.id] = []
            const sourcePrograms = []
            const existingPrograms =
              isLazyLoad && typeof allPrograms[channel.id] !== 'undefined'
                ? allPrograms[channel.id]
                : []
            const uniqueProgramsIds = {}
            existingPrograms.forEach(program => {
              if (program.id > 0) {
                sourcePrograms.push(program)
                uniqueProgramsIds[program.id] = true
              }
            })
            channel.events.forEach(program => {
              if (uniqueProgramsIds[program.id] !== true) {
                sourcePrograms.push({
                  id: program.id,
                  name: program.name,
                  desc: program.description,
                  start: program.start,
                  end: program.end,
                  dvr: false,
                  rating: {
                    mpaa:
                      typeof program.rating_mpaa === 'string' &&
                      program.rating_mpaa.toLowerCase() !== 'none'
                        ? program.rating_mpaa
                        : '',
                    tv:
                      typeof program.rating_mpaa === 'string' &&
                      program.rating_tv.toLowerCase() !== 'none'
                        ? program.rating_tv
                        : '',
                  },
                })
              }
            })
            sourcePrograms.forEach(program => {
              if (previousProgram === null) {
                if (program.start > from) {
                  programs[channel.id].push(createDummyProgram(from, program.start - 1))
                }
              } else if (program.start > previousProgram.end + 30) {
                programs[channel.id].push(
                  createDummyProgram(previousProgram.end, program.start - 1)
                )
              }
              programs[channel.id].push(program)
              previousProgram = program
            })
            if (previousProgram === null) {
              programs[channel.id].push(createDummyProgram(from, to))
            } else if (previousProgram.end < to) {
              programs[channel.id].push(createDummyProgram(previousProgram.end + 1, to))
            }
          })
          perf.stop('channels:guide:fetch', 'retrieved channel guide data from server')
          if (auth.isLoggedIn()) {
            return Promise.all([
              dispatch(fetchProgramsSuccess(programs, from, to, isLazyLoad)),
              dispatch(loadProgramDvrs()),
            ])
          } else {
            return dispatch(fetchProgramsSuccess(programs, from, to, isLazyLoad))
          }
        } else {
          return dispatch(fetchProgramsFailure(response.reason))
        }
      },
      () =>
        dispatch(
          fetchProgramsFailure(
            getP(getState()).t('tv_channels.error_loading_channel_guide_data', {
              _: 'An error occurred while loading programs information',
            })
          )
        )
    )
}

/**
 * Actions for retrieving the DVR status for programs from the API
 */
export const FETCH_PROGRAM_DVRS_REQUEST = 'CHANNELS/PROGRAMS/FETCH_DVRS_REQUEST'
export const FETCH_PROGRAM_DVRS_SUCCESS = 'CHANNELS/PROGRAMS/FETCH_DVRS_SUCCESS'
export const FETCH_PROGRAM_DVRS_FAILURE = 'CHANNELS/PROGRAMS/FETCH_DVRS_FAILURE'

const fetchProgramDvrsRequest = () => ({
  type: FETCH_PROGRAM_DVRS_REQUEST,
})

const fetchProgramDvrsSuccess = payload => ({
  type: FETCH_PROGRAM_DVRS_SUCCESS,
  payload,
})

const fetchProgramDvrsFailure = error => ({
  type: FETCH_PROGRAM_DVRS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
})

export const loadProgramDvrs = () => (dispatch, getState) => {
  dispatch(fetchProgramDvrsRequest())

  return api
    .getDvrList()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          const programDvrs = {}
          response.results.forEach(result => {
            programDvrs[result.event_id] = result.status
          })

          return dispatch(fetchProgramDvrsSuccess(programDvrs))
        } else {
          return dispatch(fetchProgramDvrsFailure(response.reason))
        }
      },
      () =>
        dispatch(
          fetchProgramsFailure(
            getP(getState()).t('tv_channels.error_loading_dvr_status', {
              _: 'An error occurred while loading DVR status information',
            })
          )
        )
    )
}

/**
 * Actions for navigating in the channel guide
 */
export const FOCUS_PROGRAM = 'CHANNELS/FOCUS_PROGRAM'

export const focusProgram = (channelId, position) => ({
  type: FOCUS_PROGRAM,
  payload: {
    channelId: channelId,
    position: position,
  },
})

/**
 * Channel guide actions
 */
export const OPEN_CHANNEL_GUIDE = 'CHANNELS/OPEN_CHANNEL_GUIDE'
export const CLOSE_CHANNEL_GUIDE = 'CHANNELS/CLOSE_CHANNEL_GUIDE'

export const openChannelGuide = (initialFrom, initialDuration) => (
  dispatch,
  getState
) => {
  const now = Math.floor(Date.now() / 1000)
  let from = initialFrom
  if (!from || from <= 0) {
    from = now
  }

  let duration = initialDuration
  const processedFrom = roundToNearestHalfHour(from)
  if (!duration || duration < 1) {
    duration = 72
  }

  const state = getState()
  const programsIsFetching = getProgramsIsFetching(state)
  const programsTo = getProgramsTo(state)

  if (
    !programsIsFetching &&
    from >= programsTo - 6 * 3600 &&
    programsTo < now + 7 * 24 * 3600
  ) {
    dispatch(loadPrograms(programsTo))
  }

  return dispatch({
    type: OPEN_CHANNEL_GUIDE,
    payload: {
      from: processedFrom,
      duration: duration,
    },
  })
}

export const closeChannelGuide = () => ({
  type: CLOSE_CHANNEL_GUIDE,
})

/**
 * Channel banner actions
 */
export const OPEN_CHANNEL_BANNER = 'CHANNELS/OPEN_CHANNEL_BANNER'
export const CLOSE_CHANNEL_BANNER = 'CHANNELS/CLOSE_CHANNEL_BANNER'

export const openChannelBanner = () => ({
  type: OPEN_CHANNEL_BANNER,
})

export const closeChannelBanner = () => ({
  type: CLOSE_CHANNEL_BANNER,
})

/**
 * Actions related to changing a channel by number
 */
export const SHOW_CHANNEL_CHANGER = 'CHANNELS/SHOW_CHANNEL_CHANGER'
export const HIDE_CHANNEL_CHANGER = 'CHANNELS/HIDE_CHANNEL_CHANGER'

const showChannelChangerInternal = channelNumber => dispatch => {
  dispatch(removeTimeout(SHOW_CHANNEL_CHANGER))
  dispatch(
    addTimeout(2500, SHOW_CHANNEL_CHANGER, () => {
      dispatch(hideChannelChanger())
    })
  )
  dispatch({
    type: SHOW_CHANNEL_CHANGER,
    payload: {
      channelNumber: channelNumber,
      isError: false,
    },
  })
}

const errorChannelChangerInternal = channelNumber => (dispatch, getState) => {
  dispatch(removeTimeout(SHOW_CHANNEL_CHANGER))
  dispatch({
    type: SHOW_CHANNEL_CHANGER,
    payload: {
      channelNumber: channelNumber,
      isError: false,
    },
  })
  setTimeout(() => {
    dispatch(removeTimeout(SHOW_CHANNEL_CHANGER))
    dispatch(
      addTimeout(750, SHOW_CHANNEL_CHANGER, () => {
        dispatch(hideChannelChangerInternal())
      })
    )
    dispatch({
      type: SHOW_CHANNEL_CHANGER,
      payload: {
        channelNumber: '',
        isError: true,
      },
    })
  }, 250)
}

const hideChannelChangerInternal = () => dispatch => {
  dispatch(removeTimeout(SHOW_CHANNEL_CHANGER))
  dispatch({
    type: HIDE_CHANNEL_CHANGER,
  })
}

export const showChannelChanger = digit => (dispatch, getState) => {
  // If any other character than a single number digit, do nothing.
  if (['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].indexOf(digit) === -1) {
    return Promise.resolve()
  }

  // Only show channel changer if in particular view mode
  if (
    [VIEW_MODE_CHANNEL_VIEW, VIEW_MODE_CHANNEL_GUIDE, VIEW_MODE_CHANNEL_BANNER].indexOf(
      getState().channels.viewMode
    ) === -1
  ) {
    return Promise.resolve()
  }

  let newChannelNumber = getState().channels.channelChangerNumber + digit
  if (newChannelNumber.length === 4) {
    const parsedChannelNumber = parseInt(newChannelNumber, 10)
    if (!isNaN(parsedChannelNumber) && getState().channels.byNo[parsedChannelNumber]) {
      dispatch(showChannelChangerInternal(newChannelNumber))
      dispatch(
        selectChannel(
          getState().channels.byNo[parsedChannelNumber],
          getState().channels.viewMode === VIEW_MODE_CHANNEL_GUIDE
            ? VIEW_MODE_CHANNEL_GUIDE
            : VIEW_MODE_CHANNEL_BANNER
        )
      )
    } else {
      dispatch(errorChannelChangerInternal(newChannelNumber))
    }
  } else {
    if (newChannelNumber.length > 4) {
      newChannelNumber = digit
    }
    dispatch(showChannelChangerInternal(newChannelNumber))
  }
}

export const hideChannelChanger = () => (dispatch, getState) => {
  if (getState().channels.channelChangerNumber.length < 4) {
    const parsedChannelNumber = parseInt(getState().channels.channelChangerNumber, 10)
    if (!isNaN(parsedChannelNumber) && getState().channels.byNo[parsedChannelNumber]) {
      dispatch(
        selectChannel(
          getState().channels.byNo[parsedChannelNumber],
          getState().channels.viewMode === VIEW_MODE_CHANNEL_GUIDE
            ? VIEW_MODE_CHANNEL_GUIDE
            : VIEW_MODE_CHANNEL_BANNER
        )
      )
      dispatch(hideChannelChangerInternal())
    } else {
      dispatch(errorChannelChangerInternal(getState().channels.channelChangerNumber))
    }
  } else {
    dispatch(hideChannelChangerInternal())
  }
}

/**
 * Actions related to Closed Captions
 */
export const ACTIVATE_CLOSED_CAPTIONS = 'CHANNELS/CLOSED_CAPTIONS/ACTIVATE'
export const INACTIVATE_CLOSED_CAPTIONS = 'CHANNELS/CLOSED_CAPTIONS/ACTIVATE'
export const SHOW_CLOSED_CAPTIONS = 'CHANNELS/CLOSED_CAPTIONS/SHOW'
export const HIDE_CLOSED_CAPTIONS = 'CHANNELS/CLOSED_CAPTIONS/HIDE'

export const activateClosedCaptions = () => ({
  type: ACTIVATE_CLOSED_CAPTIONS,
})
export const inactivateClosedCaptions = () => ({
  type: INACTIVATE_CLOSED_CAPTIONS,
})
export const showClosedCaptions = () => (dispatch, getState) => {
  const selectedChannelId = getSelectedId(getState())
  if (selectedChannelId !== -1) {
    showClosedCaptionsForChannelId(selectedChannelId)
  }
  dispatch({
    type: SHOW_CLOSED_CAPTIONS,
  })
}
export const hideClosedCaptions = () => (dispatch, getState) => {
  const selectedChannelId = getSelectedId(getState())
  if (selectedChannelId !== -1) {
    hideClosedCaptionsForChannelId(selectedChannelId)
  }
  dispatch({
    type: HIDE_CLOSED_CAPTIONS,
  })
}

/**
 * Actions related to scheduling a recording
 */
export const REQUEST_DVR = 'CHANNELS/PROGRAMS/REQUEST_DVR'
export const CANCEL_REQUESTED_DVR = 'CHANNELS/PROGRAMS/CANCEL_REQUESTED_DVR'
export const SCHEDULE_DVR_REQUEST = 'CHANNELS/PROGRAMS/SCHEDULE_DVR_REQUEST'
export const SCHEDULE_DVR_SUCCESS = 'CHANNELS/PROGRAMS/SCHEDULE_DVR_SUCCESS'
export const SCHEDULE_DVR_FAILURE = 'CHANNELS/PROGRAMS/SCHEDULE_DVR_FAILURE'

export const requestDvr = (channel, program) => ({
  type: REQUEST_DVR,
  payload: {
    channel,
    program,
  },
})

export const cancelRequestedDvr = () => ({
  type: CANCEL_REQUESTED_DVR,
})

const scheduleDvrRequest = (channelId, programId) => ({
  type: SCHEDULE_DVR_REQUEST,
  payload: {
    channelId,
    programId,
  },
})

const scheduleDvrSuccess = (channelId, programId) => ({
  type: SCHEDULE_DVR_SUCCESS,
  payload: {
    channelId,
    programId,
  },
})

const scheduleDvrFailure = (channelId, programId, error) => ({
  type: SCHEDULE_DVR_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  meta: {
    channelId,
    programId,
  },
  error: true,
})

export const scheduleDvr = (channelId, programId) => (dispatch, getState) => {
  dispatch(scheduleDvrRequest(channelId, programId))
  return api
    .scheduleDvr(programId)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(scheduleDvrSuccess(channelId, programId))
        } else {
          dispatch(loadProgramDvrs())
          return dispatch(scheduleDvrFailure(channelId, programId, response.reason))
        }
      },
      () =>
        dispatch(
          scheduleDvrFailure(
            channelId,
            programId,
            getP(getState()).t('tv_channels.error_scheduling_dvr', {
              _: 'A communication error occurred while trying to schedule the recording',
            })
          )
        )
    )
}

/**
 * Actions related to filtering channel guide by category
 */
export const RESET_FILTER_BY_CATEGORY = 'CHANNELS/PROGRAMS/RESET_FILTER_BY_CATEGORY'
export const FILTER_BY_CATEGORY = 'CHANNELS/PROGRAMS/FILTER_BY_CATEGORY'

export const resetFilterByCategory = () => ({
  type: RESET_FILTER_BY_CATEGORY,
})

export const filterByCategory = category => ({
  type: FILTER_BY_CATEGORY,
  payload: category,
})

/**
 * Update playing status
 */
export const UPDATE_PLAYING_STATUS = 'CHANNELS/PLAYING_STATUS/UPDATE'

export const updatePlayingStatus = () => ({
  type: UPDATE_PLAYING_STATUS,
})

/**
 * Update social broadcasters
 */
export const UPDATE_SOCIAL_BROADCASTERS = 'CHANNELS/SOCIAL_BROADCASTERS/UPDATE'

const resetSocialBroadcasters = () => ({
  type: UPDATE_SOCIAL_BROADCASTERS,
  payload: {
    socialBroadcasters: false,
  },
})
export const updateSocialBroadcasters = socialBroadcasters => (dispatch, getState) => {
  dispatch({
    type: UPDATE_SOCIAL_BROADCASTERS,
    payload: {
      socialBroadcasters,
    },
  })
  return dispatch(loadChannels())
    .then(() => dispatch(selectChannel(-1, VIEW_MODE_CHANNEL_GUIDE)))
    .then(() => dispatch(loadPrograms()))
}

/**
 * Actions for fetching featured channel list
 */
export const FETCH_FEATURED_CHANNELS_REQUEST = 'CHANNELS/FEATURED/FETCH_CHANNELS_REQUEST'
export const FETCH_FEATURED_CHANNELS_SUCCESS = 'CHANNELS/FEATURED/FETCH_CHANNELS_SUCCESS'
export const FETCH_FEATURED_CHANNELS_FAILURE = 'CHANNELS/FEATURED/FETCH_CHANNELS_FAILURE'

const fetchFeaturedChannelsRequest = () => ({
  type: FETCH_FEATURED_CHANNELS_REQUEST,
})

const fetchFeaturedChannelsSuccess = payload => ({
  type: FETCH_FEATURED_CHANNELS_SUCCESS,
  payload,
})

const fetchFeaturedChannelsFailure = error => ({
  type: FETCH_FEATURED_CHANNELS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
})

export const loadFeaturedChannels = () => (dispatch, getState) => {
  perf.start('featured:list:fetch')
  dispatch(fetchFeaturedChannelsRequest())
  return api
    .getFeaturedChannels()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          const channels = response.channels.map(channel => channel.id)
          perf.stop(
            'featured:list:fetch',
            'retrieved list of featured channels from server'
          )
          return dispatch(fetchFeaturedChannelsSuccess(channels))
        } else {
          return dispatch(fetchFeaturedChannelsFailure(response.reason))
        }
      },
      () =>
        dispatch(
          fetchFeaturedChannelsFailure(
            getP(getState()).t('tv_channels.error_retrieving_featured_channel_list', {
              _: 'An error occurred while retrieving featured channel list',
            })
          )
        )
    )
}

/**
 * Actions for navigating in the featured channels list
 */
export const FOCUS_FEATURED_POSITION = 'CHANNELS/FEATURED/FOCUS_POSITION'

export const focusFeaturedPosition = position => ({
  type: FOCUS_FEATURED_POSITION,
  payload: { position },
})

/**
 * Actions for loading the preview URL for a channel
 */
export const FETCH_PREVIEW_URL_REQUEST = 'CHANNELS/FETCH/PREVIEW_URL/REQUEST'
export const FETCH_PREVIEW_URL_SUCCESS = 'CHANNELS/FETCH/PREVIEW_URL/SUCCESS'
export const FETCH_PREVIEW_URL_FAILURE = 'CHANNELS/FETCH/PREVIEW_URL/FAILURE'

const fetchPreviewUrlRequest = channelId => ({
  type: FETCH_PREVIEW_URL_REQUEST,
  payload: {
    channelId,
  },
})

const fetchPreviewUrlSuccess = (channelId, previewUrl) => ({
  type: FETCH_PREVIEW_URL_SUCCESS,
  payload: {
    channelId,
    previewUrl,
  },
})

const fetchPreviewUrlFailure = (channelId, error) => ({
  type: FETCH_PREVIEW_URL_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  meta: {
    channelId,
  },
  error: true,
})

export const loadPreviewUrl = channelId => (dispatch, getState) => {
  dispatch(fetchPreviewUrlRequest(channelId))
  return api
    .getPreviewUrl(channelId)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(fetchPreviewUrlSuccess(channelId, response.preview_url))
        } else {
          return dispatch(fetchPreviewUrlFailure(channelId, response.reason))
        }
      },
      () =>
        dispatch(
          fetchPreviewUrlFailure(
            channelId,
            getP(getState()).t('tv_channels.error_retrieving_preview_url', {
              _: 'An error occurred while retrieving preview URL',
            })
          )
        )
    )
}

// Check for AutoPlay
export const checkAutoPlay = () => dispatch => {
  canAutoPlay.video().then(({ result }) => {
    if (result === true) {
      dispatch({
        type: TOGGLE_MUTED,
        muted: false,
      })
    } else {
      dispatch({
        type: TOGGLE_MUTED,
        muted: true,
      })
    }
  })
}

// Toggle mute video
export const toggleVideoMute = () => (dispatch, getState) => {
  const prevMutedState = getState().channels.videoMuted

  dispatch({
    type: TOGGLE_MUTED,
    muted: !prevMutedState,
  })
}

// View channel on fullscreen mode
export const viewChannelOnFullscreen = screen => dispatch => {
  dispatch({
    type: FULLSCREEN_MODE,
    screen,
  })
}
